import { useContext, useEffect, useMemo } from 'react';
import UserContext from '../../../common/utils/getContext';
import parse from 'html-react-parser';
import { useWindowDimensions } from '../../../common/utils/windowDimensions';
import Loader from '../../../widgets/plug/Loader/Loader';
import Language, { LangContext } from '../../../common/decorators/Language';
import { t } from '../../../common/components/T';
import GalleryBlock from '../components/GalleryBlock/GalleryBlock';
import Layout from '../components/Layout/Layout';
import PreviewButtons from '../components/PreviewButtons/PreviewButtons';
import css from './PreviewCurrentPage.module.scss';
import classnames from 'classnames';
import uikit from '../../../project/uikit/uikit.module.scss';
import { plugFileImage } from '../../../widgets/form/components/form-image/helpers';
import { A } from '../../../hybrid/wrappers';

const PreviewCurrentPage = ({ hideButtons, seoData, translates, content, admin, canonical }) => {
    const { translation } = useContext(LangContext) || {};
    const translatesData = admin ? translation : translates;
    const { lang, userToken } = useContext(UserContext);
    const breadcrumbs = useMemo(() => {
        if (admin) {
            return [
                {
                    title: t('mainPageTitle', 'Главная', 'all', translatesData),
                    link: '/'
                },
                {
                    title: t('adminTitle', 'Панель управления', 'all', translatesData),
                    link: '/admin'
                },
                {
                    title: t('admin_pagemanagement_breadcrumbs', 'Управление страницами', '/page', translatesData),
                    link: '/admin/pagemanagement'
                },
                {
                    title: t('admin_pagemanagement_breadcrumbs-create', 'Создание страницы', '/page', translatesData),
                    link: '/admin/pagemanagement/create',
                    onClick: () => {
                        admin(false);
                    }
                },
                {
                    title: t('admin_preview_breadcrumbs', 'Страница предпросмотра', '/page', translatesData),
                    link: `/page/${content.url}`
                }
            ];
        }
        return [
            {
                title: t('mainPageTitle', 'Главная', 'all', translatesData),
                link: '/'
            },
            {
                title: content?.title?.[lang] || content?.title?.default,
                link: `/page/${content?.url}`
            }
        ];
    }, [admin, translatesData, content, lang]);
    const template = content?.template;
    const pageBlocks = useMemo(() => {
        if (content?.pageContent[lang]) {
            return content?.pageContent[lang];
        }
        return content?.pageContent?.default;
    }, [content?.pageContent, lang]);
    const widthDimensions = useWindowDimensions();
    const mobile = useMemo(() => widthDimensions < 992, [widthDimensions]);

    function getInfoAboutFile(param) {
        const name = param?.split('.').splice(0, param?.split('.')?.length - 1);
        const type = param?.split('.')?.reverse()[0];
        return [name, type];
    }

    useEffect(() => {
        window?.scrollTo(0, 0);
    }, []);
    if (!content) return <Loader />;
    return (
        <Language page={'/page'} translates={translatesData}>
            <Layout
                canonical={canonical}
                seoData={seoData}
                translates={translates}
                breadcrumbs={breadcrumbs}
                template={template}
                title={content.title[lang] ? content.title[lang] : content.title.default}
            >
                <div
                    style={{
                        ...(template === 'templatecabinet' ? { padding: 0 } : {})
                    }}
                    className={classnames(css.navWrapper)}
                >
                    {template === 'templatecabinet' ? null : (
                        <p style={{ lineHeight: '1.16' }} className={uikit.h3}>
                            {content.title[lang] ? content.title[lang] : content.title.default}
                        </p>
                    )}
                    {userToken && admin && !hideButtons ? (
                        <PreviewButtons
                            widthDimensions={widthDimensions}
                            mobile={mobile}
                            translatesData={translatesData}
                            admin={admin}
                            template={template}
                        />
                    ) : null}
                </div>
                {!!pageBlocks?.length && (
                    <div
                        className={
                            content.template === 'stretched'
                                ? {}
                                : content.template === 'templateleft' || content.template === 'templateright'
                                ? css.mainContentWrapperTemplate
                                : css.mainContentWrapper
                        }
                    >
                        <div
                            className={
                                content.template === 'templateleft'
                                    ? css.leftTemplate
                                    : content.template === 'templateright'
                                    ? css.rightTemplate
                                    : ''
                            }
                        >
                            {pageBlocks
                                .filter((block) => block.template === 'leftTemplate')
                                .map((block, i) => (
                                    <div
                                        key={`pageBlocks_${i}`}
                                        className={classnames(content.template === 'stretched' ? null : css.blockContent)}
                                        style={{
                                            ...(block.type === 'textBlock' &&
                                            typeof block.blockContent === 'string' &&
                                            block?.blockContent?.trim() === ''
                                                ? { marginBottom: 0 }
                                                : {})
                                        }}
                                    >
                                        {block.type === 'textBlock' &&
                                        typeof block.blockContent === 'string' &&
                                        block?.blockContent?.trim() !== '' ? (
                                            <div className={css.ckeditorInnerPreview}>{parse(block?.blockContent || '')}</div>
                                        ) : (
                                            typeof block.blockContent === 'string' && parse(block?.blockContent || '')
                                        )}
                                        {block.type === 'galleryBlock' && <GalleryBlock template={template} block={block} />}
                                        {block.type === 'fileBlock' && (
                                            <>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap'
                                                    }}
                                                >
                                                    {block?.blockContent?.length &&
                                                        block?.blockContent?.map((file, ind) => {
                                                            const [name, type] = getInfoAboutFile(file.name || file.path);
                                                            const size = Math.round(file?.size / 1024);
                                                            return (
                                                                <div
                                                                    key={`blockContent${i}_${ind}`}
                                                                    className={css.fileWrapper}
                                                                    title={name}
                                                                >
                                                                    <div className={css.fileContainer}>
                                                                        <div className={css.filePosition}>
                                                                            <div>{plugFileImage(56, type?.toUpperCase())}</div>
                                                                            <div>
                                                                                <p className={css.subNamePosition}>{name}</p>
                                                                                {!isNaN(size) && (
                                                                                    <p className={css.subSizePosition}>
                                                                                        {t(
                                                                                            'pagemanagement-Size',
                                                                                            'Размер',
                                                                                            '/page',
                                                                                            translatesData
                                                                                        ) + ': '}
                                                                                        {size +
                                                                                            t(
                                                                                                'pagemanagement-Kb',
                                                                                                'КБ',
                                                                                                '/page',
                                                                                                translatesData
                                                                                            )}
                                                                                    </p>
                                                                                )}
                                                                                <p className={css.subType2Position}>
                                                                                    {t(
                                                                                        'pagemanagement-Format',
                                                                                        'Формат',
                                                                                        '/page',
                                                                                        translatesData
                                                                                    )}
                                                                                    {': .'}
                                                                                    {type}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <div className={css.buttonFile}>
                                                                            <A href={file.url} download={file.fileName}>
                                                                                {t(
                                                                                    'pagemanagement-Download',
                                                                                    'Скачать',
                                                                                    '/page',
                                                                                    translatesData
                                                                                )}
                                                                            </A>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                        </div>
                        <div
                            className={
                                template === 'templateleft'
                                    ? css.rightTemplate
                                    : template === 'templateright'
                                    ? css.leftTemplate
                                    : ''
                            }
                        >
                            {pageBlocks
                                .filter((block) => block.template === 'rightTemplate')
                                .map((block, ind) => (
                                    <div
                                        key={`pageBlocks_blockContent_${ind}`}
                                        style={{
                                            marginLeft: '1rem',
                                            height: '100%',
                                            display: 'flex',
                                            ...(ind + 1 ===
                                            pageBlocks.filter((block) => block.template === 'rightTemplate').length
                                                ? { marginBottom: 0 }
                                                : {})
                                        }}
                                        className={classnames(css.blockContent)}
                                    >
                                        {block.type === 'textBlock' &&
                                        typeof block.blockContent === 'string' &&
                                        block?.blockContent?.trim() !== '' ? (
                                            <div className={css.ckeditorInnerPreview}>{parse(block?.blockContent || '')}</div>
                                        ) : (
                                            typeof block.blockContent === 'string' && parse(block?.blockContent || '')
                                        )}
                                        {block.type === 'galleryBlock' && <GalleryBlock template={template} block={block} />}
                                        {block.type === 'fileBlock' && (
                                            <>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap'
                                                    }}
                                                >
                                                    {block?.blockContent?.length &&
                                                        block.blockContent.map((file, index) => {
                                                            const [name, type] = getInfoAboutFile(file.fileName);
                                                            const size = Math.round(file.size / 1024);
                                                            return (
                                                                <div
                                                                    key={`block_pageBlocks_blockContent_${index}`}
                                                                    className={css.fileWrapper}
                                                                >
                                                                    <div className={css.fileContainer}>
                                                                        <div className={css.filePosition}>
                                                                            <div>{plugFileImage(56, type?.toUpperCase())}</div>
                                                                            <div>
                                                                                <p className={css.subNamePosition}>{name}</p>
                                                                                <p className={css.subSizePosition}>
                                                                                    {t(
                                                                                        'pagemanagement-Size',
                                                                                        'Размер',
                                                                                        '/page',
                                                                                        translatesData
                                                                                    ) + ': '}
                                                                                    {size +
                                                                                        t(
                                                                                            'pagemanagement-Kb',
                                                                                            'КБ',
                                                                                            '/page',
                                                                                            translatesData
                                                                                        )}
                                                                                </p>
                                                                                <p className={css.subType2Position}>
                                                                                    {t(
                                                                                        'pagemanagement-Format',
                                                                                        'Формат',
                                                                                        '/page',
                                                                                        translatesData
                                                                                    )}
                                                                                    {': .'}
                                                                                    {type}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <div className={css.buttonFile}>
                                                                            <A href={file.url} download={file.fileName}>
                                                                                {t(
                                                                                    'pagemanagement-Download',
                                                                                    'Скачать',
                                                                                    '/page',
                                                                                    translatesData
                                                                                )}
                                                                            </A>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </Layout>
        </Language>
    );
};
export default PreviewCurrentPage;
